// Regras puras do cadastro de cliente — espelham core/migrations/023_clientes.sql.
// Nada de Supabase aqui: documento, telefone e a régua do que é cadastro
// suficiente são determinísticos e testáveis.
//
// POR QUE VALIDAR O DÍGITO DO CPF, e não só o tamanho.
// O banco garante 11 ou 14 dígitos; isso impede lixo, não impede erro de
// digitação. E erro de digitação aqui tem consequência específica: o CPF é a
// chave que evita cadastro duplicado (índice único na migração). Um dígito
// trocado cria um segundo "João Silva" que o sistema jura ser outra pessoa, e o
// histórico de compras — a razão de tudo isto existir — se parte em dois sem
// ninguém notar.
//
// A validação mora aqui, e não no banco, porque aqui dá para EXPLICAR o erro
// enquanto a pessoa digita. O banco fica com a régua que ninguém contorna.

/** Só os dígitos. Espelha norm_code() do banco para documento e telefone. */
export function soDigitos(v: string | null | undefined): string {
  return (v ?? '').replace(/\D/g, '');
}

/**
 * Dígitos verificadores do CPF. O algoritmo é o oficial: cada dígito pesa de
 * 10 a 2 (depois 11 a 2), soma, resto por 11; resto menor que 2 vira 0.
 */
function cpfValido(d: string): boolean {
  // 111.111.111-11 e afins passam na conta e não são CPF de ninguém — são o
  // que alguém digita quando quer preencher o campo sem preencher o campo.
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const digito = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

/** CNPJ existe aqui porque oficina e frota compram no balcão com nota. */
function cnpjValido(d: string): boolean {
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const digito = (pesos: number[]): number => {
    const soma = pesos.reduce((s, p, i) => s + Number(d[i]) * p, 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  const d1 = digito([5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = digito([6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === Number(d[12]) && d2 === Number(d[13]);
}

/** CPF (11) ou CNPJ (14), com dígito verificador conferido. */
export function documentoValido(v: string | null | undefined): boolean {
  const d = soDigitos(v);
  if (d.length === 11) return cpfValido(d);
  if (d.length === 14) return cnpjValido(d);
  return false;
}

/** 000.000.000-00 ou 00.000.000/0000-00. Texto incompleto sai como está —
 *  a máscara não pode brigar com quem ainda está digitando. */
export function formatarDocumento(v: string | null | undefined): string {
  const d = soDigitos(v);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return (v ?? '').trim();
}

/**
 * (53) 99988-7766 ou (53) 3232-1010. Sem DDD (8 ou 9 dígitos), formata o que
 * dá — o balcão anota telefone do jeito que o cliente fala, e recusar a
 * formatação é melhor do que inventar um DDD.
 */
export function formatarTelefone(v: string | null | undefined): string {
  const d = soDigitos(v);
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  if (d.length === 9) return d.replace(/(\d{5})(\d{4})/, '$1-$2');
  if (d.length === 8) return d.replace(/(\d{4})(\d{4})/, '$1-$2');
  return (v ?? '').trim();
}

export interface ClienteRascunho {
  nome: string;
  cpf?: string | null;
  telefone?: string | null;
}

/**
 * O que impede o cadastro de ser salvo. Devolve a mensagem, ou null quando
 * está bom — a tela mostra a primeira que aparecer.
 *
 * SÓ O NOME É OBRIGATÓRIO, e isso é decisão de balcão. Muita venda acontece com
 * o cliente sem documento no bolso; exigir CPF faria o vendedor pular o
 * cadastro inteiro, e aí não sobra histórico nenhum. Documento e telefone,
 * quando vierem, precisam estar certos — inválido é pior que ausente, porque
 * parece dado.
 */
export function problemaNoCliente(c: ClienteRascunho): string | null {
  if (!c.nome?.trim()) return 'Informe o nome do cliente.';
  if (c.nome.trim().length < 2) return 'O nome está curto demais para identificar alguém.';

  const doc = soDigitos(c.cpf);
  if (doc && !documentoValido(doc)) {
    return doc.length === 11 || doc.length === 14
      ? 'CPF/CNPJ inválido — confira os números.'
      : 'CPF precisa ter 11 dígitos (CNPJ, 14).';
  }

  const fone = soDigitos(c.telefone);
  if (fone && (fone.length < 8 || fone.length > 11)) {
    return 'Telefone incompleto — informe com DDD.';
  }
  return null;
}
